import { useCallback } from "react";
import { MailCheck, RefreshCw } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import withRoleCheck from "components/hocs/withRoleCheck";
import MxDomainService from "services/MxDomains";
import {
  MX_DOMAINS_TABLE_CONSTANTS,
  invalidateMxDomainsTableQuery,
  useMxDomainsTable,
  useRevalidateAllMxDomains,
} from "hooks/useMxDomainsTable";

// TableTop renders a string headerIcon through Semantic's Icon; passing a node
// keeps this page free of Semantic UI.
const headerIcon = (
  <span
    className="tw:inline-flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-primary-foreground"
    aria-hidden="true"
  >
    <MailCheck className="tw:size-4" />
  </span>
);

const RevalidateAllButton = withRoleCheck(() => {
  const revalidateAll = useRevalidateAllMxDomains();

  return (
    <Button
      size="sm"
      disabled={revalidateAll.isPending}
      onClick={() => revalidateAll.mutate()}
    >
      {revalidateAll.isPending && <Spinner />}
      Re-run MX Domain Check
    </Button>
  );
}, [ACL_RELATIONSHIPS.mxDomains.revalidateAll]);

const MxDomainsTableV3 = () => {
  const { columns, filters } = useMxDomainsTable();

  const fetchMxDomains = useCallback(
    queryParams =>
      MxDomainService.getTable(getNextQueryParams(queryParams, columns)),
    [columns]
  );

  return (
    <BaseTableV3
      columns={columns}
      filters={filters}
      queryKey={MX_DOMAINS_TABLE_CONSTANTS.queryKey}
      className={MX_DOMAINS_TABLE_CONSTANTS.className}
      tableName={MX_DOMAINS_TABLE_CONSTANTS.tableName}
      exportTableName={MX_DOMAINS_TABLE_CONSTANTS.exportTableName}
      header={MX_DOMAINS_TABLE_CONSTANTS.header}
      headerIcon={headerIcon}
      enableSearch
      enableSettings
      createButton={
        <div className="tw:flex tw:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={invalidateMxDomainsTableQuery}
          >
            <RefreshCw />
            Refresh
          </Button>
          <RevalidateAllButton />
        </div>
      }
      fetchData={fetchMxDomains}
      invalidateData={invalidateMxDomainsTableQuery}
    />
  );
};

export default MxDomainsTableV3;
